import { useEffect, useRef, useState } from "react";
import type { Asset, Me, Trip } from "../../lib/api.ts";
import { navigate, tripPath } from "../../lib/router.tsx";
import { tabKeys, tabStop } from "../../ui/tabs.ts";
import { MediaTab } from "./MediaTab.tsx";
import { TagsTab } from "./TagsTab.tsx";

/**
 * The trip's footage, two ways.
 *
 * **Media** is the day-by-day grid and is where you land, because the usual question is "what
 * did we shoot today". **Tags** is the same footage filed into folders — a tag is a label, and
 * a tag with a note on it is an idea.
 *
 * Two tabs and a swipe between them, rather than a menu: they are two views of one thing, and
 * a view you can reach with a thumb is a view people use. Each is still a real URL, so a
 * folder can be linked and the back button behaves.
 */

export type LibraryTab = "media" | "tags";
const ORDER: LibraryTab[] = ["media", "tags"];

/** How far a thumb must travel sideways before it is a swipe rather than a wandering tap. */
const SWIPE_PX = 64;
/** And how much more sideways than down, so scrolling the grid is never a tab change. */
const SIDEWAYS = 1.6;

export function MediaLibrary({
  trip,
  me,
  tab,
  itemId,
  day,
  onTab,
  onItem,
  onOpen,
}: {
  trip: Trip;
  me: Me;
  tab: LibraryTab;
  /** The folder being looked at inside Tags, if any. */
  itemId: string | null;
  /**
   * Which day of footage to show, when the host has a clock of its own. Passed straight through
   * to the grid, which then draws no day tabs: the editor's map has one strip of days above both
   * its panels, and a library answering the same question again a few inches to the right is how
   * two parts of one screen come to disagree about what day it is.
   */
  day?: string | null;
  /**
   * What picking a tab means. By default it is a route, because each view is a real URL that can
   * be linked and come back to. A host that shows the library inside a page of its own passes
   * its own: an editor has no library route to navigate to — their shell does not carry one.
   */
  onTab?: (t: LibraryTab) => void;
  /**
   * What opening a tag folder means, for the same reason: a host with no library route of its
   * own cannot send the reader to one, and on the editor's map doing so took the screen away
   * from the map the page exists to show.
   */
  onItem?: (tagId: string | null) => void;
  /** Where a tapped recording is read, when the host has somewhere of its own. */
  onOpen?: (asset: Asset) => void;
}) {
  const from = useRef<{ x: number; y: number } | null>(null);
  // While recordings are being picked, the screen belongs to that: a swipe to Tags would throw
  // the selection away, and the tabs that offer it would be inviting the mistake.
  const [picking, setPicking] = useState(false);
  // Inside a folder the swipe is off: you are a level deeper than the tabs, and sliding
  // sideways out of a folder you opened is not what the gesture means there.
  const inFolder = itemId !== null;

  useEffect(() => {
    if (inFolder || picking) return;

    /**
     * Something the thumb started on that scrolls sideways itself — the day strip, a row of
     * chips. A swipe there means to move it, not to change tab.
     */
    const onSidewaysScroller = (target: EventTarget | null) => {
      let el = target instanceof Element ? target : null;
      while (el && el !== document.body) {
        if (el.scrollWidth > el.clientWidth + 1) return true;
        el = el.parentElement;
      }
      return false;
    };

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || e.touches.length !== 1 || onSidewaysScroller(e.target)) {
        from.current = null;
        return;
      }
      from.current = { x: t.clientX, y: t.clientY };
    };

    const onEnd = (e: TouchEvent) => {
      const start = from.current;
      from.current = null;
      const t = e.changedTouches[0];
      if (!start || !t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      // Far enough, and far more sideways than down: the grid scrolls vertically and must not
      // lose a flick to the tabs.
      if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy) * SIDEWAYS) return;
      const next = ORDER[ORDER.indexOf(tab) + (dx < 0 ? 1 : -1)];
      // At either end there is nothing to swipe to, and nothing should happen.
      if (next) navigate(tripPath(trip.id, next));
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [tab, trip.id, inFolder, picking]);

  return (
    <div className="stack">
      {/* A folder is a level deeper, and it names itself in the same place the tabs sit. Two
          headers stacked would be one too many, and tabs there would offer to leave the folder
          sideways — which is not the way it was come into. */}
      {!inFolder && !picking && (
        <div className="lib-tabs" role="tablist" aria-label="Media Library" onKeyDown={tabKeys}>
          {ORDER.map((t, i) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={t === tab}
              tabIndex={tabStop(i, ORDER.indexOf(tab))}
              className={`lib-tab${t === tab ? " on" : ""}`}
              onClick={() => (onTab ? onTab(t) : navigate(tripPath(trip.id, t)))}
            >
              {t === "media" ? "Media" : "Tags"}
            </button>
          ))}
        </div>
      )}

      {tab === "media" ? (
        <MediaTab
          trip={trip}
          me={me}
          onPicking={setPicking}
          {...(day !== undefined ? { day } : {})}
          {...(onOpen ? { onOpen } : {})}
        />
      ) : (
        <TagsTab
          trip={trip}
          itemId={itemId}
          {...(onItem ? { onFolder: onItem } : {})}
          {...(onOpen ? { onOpen } : {})}
        />
      )}
    </div>
  );
}
